import { HouseIcon, RotateCwIcon, SparklesIcon, TriangleAlertIcon } from "lucide-react";
import { useRouteError } from "react-router";

import { useBrandName } from "@/api/hooks/tenant";
import { Button } from "@/components/ui/button";

import { NotFoundMessage } from "./not-found-message";
import { classifyRouteError, errorDetails } from "./route-errors";
import { FullPageStatus, StatusMessage } from "./status-screen";
import { useHomePath } from "./use-home-path";

/** Router `errorElement`: a friendly full-page screen for 404s, stale deploys and crashes. */
export function RouteErrorBoundary() {
  const error = useRouteError();
  const kind = classifyRouteError(error);

  return (
    <FullPageStatus>
      {kind === "not-found" && <NotFoundMessage />}
      {kind === "stale-build" && <StaleBuildMessage />}
      {kind === "unexpected" && <UnexpectedErrorMessage error={error} />}
    </FullPageStatus>
  );
}

function StaleBuildMessage() {
  const brand = useBrandName();
  return (
    <StatusMessage
      icon={SparklesIcon}
      title={`${brand} has been updated`}
      description="A new version was released while this tab was open. Reload the page to continue with the latest version."
      actions={<ReloadButton />}
    />
  );
}

function UnexpectedErrorMessage({ error }: { error: unknown }) {
  const homePath = useHomePath();
  const details = errorDetails(error);

  return (
    <StatusMessage
      icon={TriangleAlertIcon}
      title="Something went wrong"
      description="An unexpected error stopped this page from loading. Reloading usually fixes it."
      actions={
        <>
          <ReloadButton />
          <Button variant="outline" asChild>
            {/* A full page load, so the app starts from a clean state. */}
            <a href={homePath}>
              <HouseIcon />
              Go home
            </a>
          </Button>
        </>
      }
    >
      {details && (
        <details className="mt-8 w-full rounded-lg border bg-card text-left">
          <summary className="cursor-pointer rounded-lg px-4 py-2.5 text-sm font-medium text-muted-foreground select-none">
            Error details
          </summary>
          <pre className="border-t px-4 py-3 font-mono text-xs break-words whitespace-pre-wrap text-muted-foreground">
            {details}
          </pre>
        </details>
      )}
    </StatusMessage>
  );
}

function ReloadButton() {
  return (
    <Button onClick={() => window.location.reload()}>
      <RotateCwIcon />
      Reload
    </Button>
  );
}
